import { supabase } from "@/integrations/supabase/client";

export interface CardDrop {
  cards: Array<{
    card_id: string;
    name: string;
    rarity: string;
    artwork_url: string;
  }>;
}

export const checkCardDrops = async (
  userId: string,
  eventType: string,
  eventData: Record<string, any> = {}
): Promise<CardDrop> => {
  try {
    const { data, error } = await supabase.rpc('check_card_drops', {
      user_id_param: userId,
      event_type: eventType,
      event_data: eventData
    });

    if (error) {
      console.error('Error checking card drops:', error);
      return { cards: [] };
    }

    const cardsArray = Array.isArray(data) ? data.map((item: any) => ({
      card_id: item.card_id || '',
      name: item.name || '',
      rarity: item.rarity || 'common',
      artwork_url: item.artwork_url || ''
    })) : [];

    if (typeof window !== 'undefined' && cardsArray.length > 0) {
      window.dispatchEvent(new CustomEvent('cards-awarded', {
        detail: { cards: cardsArray, eventType, eventData }
      }));
    }
    
    return { cards: cardsArray };
  } catch (error) {
    console.error('Error in checkCardDrops:', error);
    return { cards: [] };
  }
};

// Enhanced trigger functions for new event types
export const triggerXPMilestoneCheck = async (userId: string, currentXP: number) => {
  return checkCardDrops(userId, 'xp_milestone', { current_xp: currentXP });
};

export const triggerXPIntervalCheck = async (userId: string, currentXP: number) => {
  return checkCardDrops(userId, 'xp_interval', { current_xp: currentXP });
};

export const triggerAchievementCheck = async (userId: string, achievementId: string) => {
  return checkCardDrops(userId, 'achievement', { achievement_id: achievementId });
};

export const triggerDailyLoginCheck = async (userId: string) => {
  return checkCardDrops(userId, 'daily_login', { login_date: new Date().toISOString() });
};

export const triggerStreakCheck = async (userId: string, streakType: string, streakCount: number) => {
  return checkCardDrops(userId, 'streak', { streak_type: streakType, streak_count: streakCount });
};

// New trigger functions for enhanced card system
export const triggerPerfectDietDay = async (userId: string) => {
  return checkCardDrops(userId, 'diet_perfect_day', { 
    date: new Date().toISOString().split('T')[0],
    all_slots_truth: true 
  });
};

export const triggerFastingDuration = async (userId: string, fastingHours: number) => {
  return checkCardDrops(userId, 'fasting_duration', { 
    fasting_hours: fastingHours,
    date: new Date().toISOString()
  });
};

export const triggerExerciseDuration = async (userId: string, exerciseMinutes: number) => {
  return checkCardDrops(userId, 'exercise_duration', { 
    exercise_minutes: exerciseMinutes,
    date: new Date().toISOString()
  });
};

export const triggerOutdoorDuration = async (userId: string, outdoorHours: number) => {
  return checkCardDrops(userId, 'outdoor_duration', { 
    outdoor_hours: outdoorHours,
    date: new Date().toISOString()
  });
};

export const triggerSpecificDate = async (userId: string, targetDate: string) => {
  return checkCardDrops(userId, 'specific_date', { 
    target_date: targetDate,
    current_date: new Date().toISOString().split('T')[0]
  });
};

export const triggerDateRange = async (userId: string, startDate: string, endDate: string) => {
  const currentDate = new Date().toISOString().split('T')[0];
  return checkCardDrops(userId, 'date_range', { 
    start_date: startDate,
    end_date: endDate,
    current_date: currentDate
  });
};

export const triggerWeeklyStreak = async (userId: string, streakDays: number) => {
  return checkCardDrops(userId, 'weekly_streak', { 
    streak_days: streakDays,
    minimum_days: 7
  });
};

export const triggerMonthlyMilestone = async (userId: string, month: number) => {
  return checkCardDrops(userId, 'monthly_milestone', { 
    target_month: month,
    current_month: new Date().getMonth() + 1
  });
};

export const createCard = async (cardData: {
  name: string;
  description: string;
  artwork_url: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  xp_value: number;
  drop_weight: number;
  set_id: string;
}) => {
  try {
    const { data, error } = await supabase
      .from('cards')
      .insert([{ ...cardData, is_active: true }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating card:', error);
    throw error;
  }
};

export const getUserCards = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_cards')
      .select(`
        *,
        cards (
          id,
          name,
          description,
          artwork_url,
          rarity,
          xp_value,
          card_sets (
            name,
            theme
          )
        )
      `)
      .eq('user_id', userId)
      .order('first_obtained_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user cards:', error);
    return [];
  }
};

export const getCardCollection = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_cards')
      .select(`
        quantity,
        first_obtained_at,
        cards (
          id,
          name,
          description,
          artwork_url,
          rarity,
          xp_value,
          card_sets (
            name,
            theme,
            border_style
          )
        )
      `)
      .eq('user_id', userId)
      .order('first_obtained_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching card collection:', error);
    return [];
  }
};
